import React, { useCallback, useRef, useState } from 'react';
import { Button, Checkbox, RadioButton, RadioButtonGroup } from '@carbon/react';
import { Settings } from '@carbon/react/icons';
import { ArrowUp, ArrowDown, Close } from '@carbon/react/icons';
import { CrudPanelShell } from './CrudPanelShell';
import { useCrudConfig } from './provider';
import { CrudSort } from './urlState';
import { ColumnPrefs, CrudDensity } from './useColumnPrefs';
import { useDismissPanel } from './useDismissPanel';

export interface ColumnManagerProps {
  columns: Array<{ key: string; label: string; alwaysVisible?: boolean }>;
  prefs: ColumnPrefs;
  onChange: (next: ColumnPrefs) => void;
  /** Applied sort, precedence order (Task 7/8). The section renders only when this AND
   * `onSortChange` are both given and non-empty — see Task 8b. */
  sort?: CrudSort[];
  onSortChange?: (next: CrudSort[]) => void;
}

/** `field,DIRECTION` — the comparison Apply uses to decide whether sort actually changed. */
const sortKey = (sort: CrudSort[]) => sort.map((entry) => `${entry.field},${entry.direction}`).join('|');

/**
 * The column preferences editor (spec §6.4). Carbon's Dropdown at 1.35.0 renders ONLY its
 * `items` through the ListBox — it swallows `children` — so the brief's Dropdown-shell
 * fallback applies: a plain Button toggles a plain panel. Keyboard-first: every control is
 * a real button/input, no drag layers this phase.
 *
 * Edits are STAGED. Show/hide and reordering write to a local copy that the table does not
 * see until Apply, so a run of adjustments is one change to the table rather than a dozen.
 * Closing without applying discards them.
 */
export function ColumnManager(props: ColumnManagerProps) {
  const { translate } = useCrudConfig();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismissPanel(open, rootRef, close);

  const [draft, setDraft] = useState<ColumnPrefs>(props.prefs);
  const [draftSort, setDraftSort] = useState<CrudSort[]>(props.sort ?? []);

  // Seeding on open rather than in an effect: the panel must start from whatever the table
  // is showing NOW, including a change made elsewhere while it was closed.
  const openPanel = () => {
    setDraft(props.prefs);
    setDraftSort(props.sort ?? []);
    setOpen(true);
  };

  // Rows follow the draft's order (the reconciled prefs always cover every config key), so
  // the panel mirrors what Apply would produce.
  const byKey = new Map(props.columns.map((column) => [column.key, column]));
  const ordered = draft.order
    .map((key) => byKey.get(key))
    .filter((column): column is ColumnManagerProps['columns'][number] => Boolean(column));
  for (const column of props.columns) {
    if (!ordered.some((known) => known.key === column.key)) {
      ordered.push(column);
    }
  }

  const setVisible = (key: string, visible: boolean) => {
    setDraft({
      ...draft,
      hidden: visible ? draft.hidden.filter((hidden) => hidden !== key) : [...draft.hidden, key],
    });
  };

  const move = (key: string, offset: -1 | 1) => {
    const index = draft.order.indexOf(key);
    const target = index + offset;
    if (index < 0 || target < 0 || target >= draft.order.length) {
      return;
    }
    const order = [...draft.order];
    order[index] = order[target];
    order[target] = key;
    setDraft({ ...draft, order });
  };

  // Move up/down swaps adjacent entries; remove filters the entry out. Neither reaches
  // onSortChange before Apply (spec §5's staging rule applies here too).
  const moveSort = (index: number, offset: -1 | 1) => {
    const target = index + offset;
    if (target < 0 || target >= draftSort.length) {
      return;
    }
    const next = [...draftSort];
    next[index] = draftSort[target];
    next[target] = draftSort[index];
    setDraftSort(next);
  };

  const removeSort = (index: number) => {
    setDraftSort(draftSort.filter((_, entryIndex) => entryIndex !== index));
  };

  // Defaults are the config's own order with nothing hidden and comfortable density — what
  // a first-time visitor sees, not whatever was last applied.
  const defaults: ColumnPrefs = {
    order: props.columns.map((column) => column.key),
    hidden: [],
    density: 'comfortable',
  };
  const isDefault =
    draft.hidden.length === 0 && draft.density === 'comfortable' && draft.order.join('|') === defaults.order.join('|');

  return (
    <div className="crud-column-manager" ref={rootRef}>
      {/* size="lg" is what matches the toolbar's own 3rem row: the create button and Clear
          are Carbon's default lg, so an md here sat 8px short of them. */}
      {/*
        Icon-only: the label moves into iconDescription, which Carbon renders as the tooltip
        AND the accessible name, so the control keeps its name for screen readers.
      */}
      <Button
        kind="ghost"
        size="lg"
        hasIconOnly
        renderIcon={Settings}
        iconDescription={translate('common.columns')}
        tooltipPosition="bottom"
        className="crud-column-manager__trigger"
        onClick={() => (open ? close() : openPanel())}
        aria-expanded={open}
        aria-haspopup="true"
      />
      {open && (
        <CrudPanelShell
          className="crud-column-manager__panel"
          title={translate('common.columns')}
          resetDisabled={isDefault}
          onReset={() => setDraft(defaults)}
          onApply={() => {
            props.onChange(draft);
            // setSort also resets the page to 1 (see useCrudFilters), so an Apply that did
            // not touch sort must not call it — that would bounce the reader back to page 1.
            if (props.onSortChange && sortKey(draftSort) !== sortKey(props.sort ?? [])) {
              props.onSortChange(draftSort);
            }
            close();
          }}
        >
          {ordered.map((column, index) => (
            <div key={column.key} className="crud-column-manager__row">
              <Checkbox
                id={`crud-col-${column.key}`}
                className="crud-column-manager__toggle"
                labelText={column.label}
                checked={!draft.hidden.includes(column.key)}
                disabled={column.alwaysVisible}
                onChange={(_, { checked }) => setVisible(column.key, checked)}
              />
              {/*
                Plain buttons, not Carbon's `hasIconOnly`: that variant drags a tooltip layer
                along, and seven rows of hover tooltips inside an already-dense panel is noise.
                The aria-label carries the same name the tooltip did.
              */}
              <button
                type="button"
                className="crud-icon-button"
                aria-label={`${translate('common.moveUp')} ${column.label}`}
                disabled={index === 0}
                onClick={() => move(column.key, -1)}
              >
                <ArrowUp size={16} aria-hidden />
              </button>
              <button
                type="button"
                className="crud-icon-button"
                aria-label={`${translate('common.moveDown')} ${column.label}`}
                disabled={index === ordered.length - 1}
                onClick={() => move(column.key, 1)}
              >
                <ArrowDown size={16} aria-hidden />
              </button>
            </div>
          ))}
          {props.onSortChange && (props.sort?.length ?? 0) > 0 && (
            // Scoped to its own group: the column rows above already use "Move up <label>"
            // etc., so a column that is also sorted would otherwise get two same-named
            // buttons. role="group" + aria-label keeps the two sets distinguishable.
            // A fieldset/legend would add Carbon-unstyled native chrome; a plain div with
            // the ARIA role is what the rest of this panel's markup already does.
            // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
            <div role="group" aria-label={translate('common.sortOrder')} className="crud-column-manager__sort">
              <p className="cds--label crud-column-manager__sort-title">{translate('common.sortOrder')}</p>
              {draftSort.map((entry, index) => {
                const label = byKey.get(entry.field)?.label ?? entry.field;
                return (
                  <div key={entry.field} className="crud-column-manager__sort-row">
                    {/* One grid cell (matches the fixed button columns below it) holding three
                        separately-queryable text nodes: precedence, label, direction. */}
                    <span>
                      <span>{index + 1}.</span> <span>{label}</span>{' '}
                      <span className="crud-column-manager__sort-direction">
                        {entry.direction === 'ASC' ? translate('common.ascending') : translate('common.descending')}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="crud-icon-button"
                      aria-label={`${translate('common.moveUp')} ${label}`}
                      disabled={index === 0}
                      onClick={() => moveSort(index, -1)}
                    >
                      <ArrowUp size={16} aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="crud-icon-button"
                      aria-label={`${translate('common.moveDown')} ${label}`}
                      disabled={index === draftSort.length - 1}
                      onClick={() => moveSort(index, 1)}
                    >
                      <ArrowDown size={16} aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="crud-icon-button"
                      aria-label={`${translate('common.removeSort')} ${label}`}
                      onClick={() => removeSort(index)}
                    >
                      <Close size={16} aria-hidden />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <div className="crud-column-manager__density">
            <RadioButtonGroup
              legendText={translate('common.density')}
              name="crud-density"
              valueSelected={draft.density}
              onChange={(value) => setDraft({ ...draft, density: value as CrudDensity })}
            >
              <RadioButton labelText={translate('common.comfortable')} value="comfortable" />
              <RadioButton labelText={translate('common.compact')} value="compact" />
            </RadioButtonGroup>
          </div>
        </CrudPanelShell>
      )}
    </div>
  );
}
